import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { ApiRequestService } from '@app/_services/api-Services/api-request.service';
import { AuthService } from '@app/_services/auth/auth.service';
import { UserAuthDto } from '@app/shared/model/user.model';

@Component({
  selector: 'app-sign-in',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './sign-in.component.html',
  styleUrls: ['./sign-in.component.scss']
})
export class SignInComponent implements OnInit {
  loginForm!: FormGroup;
  captchaQuestion: string = '';
  captchaAnswer: number = 0;
  errorMessage: string = '';
  isLoading: boolean = false;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private apiService: ApiRequestService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.initializeForm();
    this.generateCaptcha();
  }

  private initializeForm(): void {
    this.loginForm = this.fb.group({
      username: ['', [Validators.required]],
      password: ['', [Validators.required, Validators.minLength(6)]],
      captchaInput: ['', [Validators.required]],
      rememberMe: [false]
    });
  }

  generateCaptcha(): void {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    this.captchaAnswer = num1 + num2;
    this.captchaQuestion = `What is ${num1} + ${num2}?`;
  }

  onSubmit(): void {
    this.errorMessage = '';

    if (this.loginForm.invalid) {
      this.errorMessage = 'Please fill in all required fields correctly.';
      return;
    }

    const userCaptchaAnswer = parseInt(this.loginForm.get('captchaInput')?.value);
    if (userCaptchaAnswer !== this.captchaAnswer) {
      this.errorMessage = 'Captcha answer is incorrect. Please try again.';
      this.generateCaptcha();
      this.loginForm.patchValue({ captchaInput: '' });
      return;
    }

    this.isLoading = true;
    this.performLogin();
  }

  private performLogin(): void {
    const { username, password, rememberMe } = this.loginForm.value;

    const payload = {
      username: username,
      password: password
    };

    this.apiService.postRequest<any>('Authentication', payload)
      .subscribe({
        next: (response) => {
          if (response.status === 200 && response.body) {
            const authData = response.body;
            
            // Map the response to UserAuthDto
            const user: UserAuthDto = {
              id: authData.id,
              userName: authData.userName,
              fullName: authData.fullName,
              emailAddress: authData.emailAddress,
              bearerToken: authData.bearerToken,
              isSuperAdmin: authData.isSuperAdmin,
              profilePhoto: authData.profilePhoto,
              claims: authData.claims || [] // Use claims from response or empty array
            };

            // Store user data and token based on Remember Me preference
            this.authService.updateCurrentUser(user, rememberMe);

            console.log('Login successful:', user);
            this.isLoading = false;
            
            // Navigate to licence management page
            this.router.navigate(['/dashboard/add-licence']);
          }
        },
        error: (error) => {
          console.error('Login error:', error);
          this.isLoading = false;
          this.errorMessage = error.error?.message || 'Login failed. Please check your credentials and try again.';
          this.generateCaptcha();
          this.loginForm.patchValue({ captchaInput: '' });
        }
      });
  }

  get f() {
    return this.loginForm.controls;
  }

  goToSignUp(): void {
    this.router.navigate(['/auth/sign-up']);
  }

  goToForgotPassword(): void {
    this.router.navigate(['/auth/reset-password']);
  }

  goToHome(): void {
    this.router.navigate(['/']);
  }
}
